import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  ServerIcon,
  CircleStackIcon,
  CloudIcon,
  LockClosedIcon,
  BeakerIcon,
  DocumentTextIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';

/**
 * Documentation sidebar navigation
 *
 * Provides navigation for technical documentation categories.
 * Follows the DocumentationPage.jsx pattern with sticky positioning
 * and active state highlighting.
 */

const docCategories = [
  {
    id: 'index',
    name: 'Overview',
    path: '/technical-docs',
    icon: HomeIcon
  },
  {
    id: 'architecture',
    name: 'Architecture',
    path: '/technical-docs/architecture',
    icon: CpuChipIcon
  },
  {
    id: 'authentication',
    name: 'Authentication',
    path: '/technical-docs/authentication',
    icon: ShieldCheckIcon
  },
  {
    id: 'agentnexus-frontend',
    name: 'AgentNexus Frontend',
    path: '/technical-docs/agentnexus-frontend',
    icon: CodeBracketIcon
  },
  {
    id: 'agentnexus-backend',
    name: 'AgentNexus Backend',
    path: '/technical-docs/agentnexus-backend',
    icon: ServerIcon
  },
  {
    id: 'nexuschat',
    name: 'NexusChat',
    path: '/technical-docs/nexuschat',
    icon: DocumentTextIcon
  },
  {
    id: 'database',
    name: 'Database',
    path: '/technical-docs/database',
    icon: CircleStackIcon
  },
  {
    id: 'deployment',
    name: 'Deployment',
    path: '/technical-docs/deployment',
    icon: CloudIcon
  },
  {
    id: 'security',
    name: 'Security',
    path: '/technical-docs/security',
    icon: LockClosedIcon
  },
  {
    id: 'testing',
    name: 'Testing',
    path: '/technical-docs/testing',
    icon: BeakerIcon
  },
  {
    id: 'use-cases',
    name: 'Use Cases',
    path: '/technical-docs/use-cases',
    icon: DocumentTextIcon
  }
];

const DocsSidebar = ({ onCategorySelect }) => {
  const location = useLocation();

  // Determine active category from current path
  const getActiveCategory = () => {
    const path = location.pathname;

    // Exact match for index
    if (path === '/technical-docs' || path === '/technical-docs/') {
      return 'index';
    }

    // Find matching category
    const category = docCategories.find(cat =>
      cat.id !== 'index' && path.startsWith(cat.path)
    );

    return category?.id || 'index';
  };

  const activeCategory = getActiveCategory();

  return (
    <nav className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 sticky top-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
        Documentation
      </h3>
      <ul className="space-y-1">
        {docCategories.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;

          return (
            <li key={category.id}>
              <Link
                to={category.path}
                onClick={() => onCategorySelect?.(category.id)}
                className={`w-full flex items-center text-left px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">{category.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Version info */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Videxa Documentation
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Internal Use Only
        </p>
      </div>
    </nav>
  );
};

export default DocsSidebar;
export { docCategories };
